import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  useCombobox,
  Combobox,
  InputBase,
  Group,
  Text,
  Button,
  ActionIcon,
  Divider,
  Stack,
  Image,
  Badge,
  Loader,
  Center,
  Box,
} from "@mantine/core";
import { IconPlus, IconX, IconBarcode, IconPackage } from "@tabler/icons-react";
import { useProducts } from "../../../../../context/ProductsContext";
import { Product } from "../../../../../types/products";
import AddProductModal from "../../../../products/AddProductModal";

interface ProductLookupProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const CHUNK_SIZE = 10;
const MAX_PRODUCTS = 50;

export default function ProductLookup({
  value,
  onChange,
  error,
}: ProductLookupProps) {
  const { searchProducts, getProductById } = useProducts();
  const selectedProduct = getProductById(value);
  const [searchQuery, setSearchQuery] = useState(
    selectedProduct ? selectedProduct.name : "",
  );
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [barcodeDetected, setBarcodeDetected] = useState(false);

  const [addModalOpened, setAddModalOpened] = useState(false);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastProductRef = useRef<HTMLDivElement | null>(null);
  const combobox = useCombobox({
    onDropdownClose: () => {
      setSearchQuery("");
      combobox.resetSelectedOption();
    },
  });

  // Barcode scanning effect
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input
      if (
        event.target === document.activeElement &&
        document.activeElement?.tagName === "INPUT"
      ) {
        return;
      }

      // Detect barcode input (rapid key presses)
      setBarcodeDetected(true);

      // Clear search and populate with barcode
      setSearchQuery(event.key);
      setCurrentPage(1);
      setDisplayedProducts([]);
      setHasMore(true);

      // Reset barcode detection after a delay
      setTimeout(() => setBarcodeDetected(false), 2000);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Search products when query changes
  useEffect(() => {
    const performSearch = async () => {
      setIsLoading(true);
      setCurrentPage(1);
      setDisplayedProducts([]);
      setHasMore(true);

      try {
        const results = await searchProducts(searchQuery, 1, CHUNK_SIZE);
        setDisplayedProducts(results);
        setHasMore(
          results.length === CHUNK_SIZE && results.length < MAX_PRODUCTS,
        );
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchProducts]);

  // Load more products when scrolling
  const loadMoreProducts = useCallback(async () => {
    if (!hasMore || isLoadingMore || displayedProducts.length >= MAX_PRODUCTS)
      return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const results = await searchProducts(searchQuery, nextPage, CHUNK_SIZE);

      if (results.length > 0) {
        setDisplayedProducts((prev) => [...prev, ...results]);
        setCurrentPage(nextPage);
        setHasMore(
          results.length === CHUNK_SIZE &&
            displayedProducts.length + results.length < MAX_PRODUCTS,
        );
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    displayedProducts.length,
    currentPage,
    searchQuery,
    searchProducts,
  ]);

  // Intersection observer for infinite scrolling
  const lastProductElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreProducts();
        }
      });

      if (node) observerRef.current.observe(node);
      lastProductRef.current = node;
    },
    [hasMore, loadMoreProducts],
  );

  const handleCreateNew = () => {
    setAddModalOpened(true);
  };

  const handleProductCreated = (productId: string) => {
    onChange(productId);
    combobox.closeDropdown();
    setSearchQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchQuery("");
  };

  const displayValue = selectedProduct
    ? `${selectedProduct.name} - ${selectedProduct.category}${selectedProduct.company ? ` | ${selectedProduct.company}` : ""}`
    : searchQuery;

  const renderProductOption = (product: Product, index: number) => {
    const isLast = index === displayedProducts.length - 1;

    return (
      <div key={product.id} ref={isLast ? lastProductElementRef : null}>
        <Combobox.Option value={product.id}>
          <Group gap="sm" align="center">
            {product.img_url ? (
              <Image
                src={product.img_url}
                w={40}
                h={40}
                fit="contain"
                alt={product.name}
              />
            ) : (
              <Box
                w={40}
                h={40}
                bg="gray.2"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                }}
              >
                <IconPackage size={20} color="gray.6" />
              </Box>
            )}

            <Stack gap={2} style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {product.name}
              </Text>
              <Group gap="xs">
                <Badge size="xs" variant="light">
                  {product.category}
                </Badge>
                {product.company && (
                  <Text size="xs" c="dimmed">
                    {product.company}
                  </Text>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                ID: {product.custom_id}
              </Text>
            </Stack>

            <Stack gap={2} align="flex-end">
              <Text size="sm" fw={500}>
                ${product.list_price.toFixed(2)}
              </Text>
              <Text size="xs" c="dimmed">
                Stock: {product.quantity}
              </Text>
            </Stack>
          </Group>
        </Combobox.Option>
      </div>
    );
  };

  return (
    <>
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          onChange(val);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            label="Product"
            required
            error={error}
            placeholder="Scan barcode or search for product..."
            onClick={() => combobox.openDropdown()}
            rightSection={
              <Group gap={4}>
                {barcodeDetected && <IconBarcode size={16} color="green" />}
                {selectedProduct ? (
                  <ActionIcon size="sm" variant="subtle" onClick={handleClear}>
                    <IconX size={14} />
                  </ActionIcon>
                ) : (
                  <Combobox.Chevron />
                )}
              </Group>
            }
            value={displayValue}
            onChange={(e) => {
              if (selectedProduct) {
                onChange("");
              }
              setSearchQuery(e.target.value);
            }}
            styles={(theme) => ({
              input: {
                "&:focus": {
                  borderColor: theme.colors.teal[5],
                },
              },
            })}
          />
        </Combobox.Target>

        {combobox.dropdownOpened && (
          <Combobox.Dropdown>
            <Combobox.Options>
              {!searchQuery && !selectedProduct ? (
                <Combobox.Empty>
                  <Stack gap="xs">
                    <Text size="sm">
                      Search for a product by name, category, company, or scan a
                      barcode
                    </Text>
                    <Divider label="Or" />
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      onClick={handleCreateNew}
                    >
                      Create New Product
                    </Button>
                  </Stack>
                </Combobox.Empty>
              ) : displayedProducts.length > 0 ? (
                <>
                  {displayedProducts.map((product, index) =>
                    renderProductOption(product, index),
                  )}

                  {isLoadingMore && (
                    <Center py="sm">
                      <Loader size="sm" />
                    </Center>
                  )}

                  {!hasMore &&
                    displayedProducts.length > 0 &&
                    displayedProducts.length >= MAX_PRODUCTS && (
                      <Box p="sm" ta="center">
                        <Text size="xs" c="dimmed">
                          {`Showing ${MAX_PRODUCTS} results. Please be more specific in your search.`}
                        </Text>
                      </Box>
                    )}
                </>
              ) : isLoading ? (
                <Center py="xl">
                  <Loader size="md" />
                </Center>
              ) : (
                <Combobox.Empty>
                  <Group justify="space-between">
                    <Text size="sm">No results found</Text>
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      onClick={handleCreateNew}
                    >
                      Create New Product
                    </Button>
                  </Group>
                </Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        )}
      </Combobox>

      <AddProductModal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        onProductCreated={handleProductCreated}
      />
    </>
  );
}
